import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, ShieldCheck, Gem, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/store/ProductCard";
import { StarRating } from "@/components/store/StarRating";
import { useLanguage } from "@/context/LanguageContext";
import api from "@/lib/api";

const HERO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_elite-baubles-1/artifacts/sf8o0isk_IMG_2571.jpeg";
const EDITORIAL = "https://images.pexels.com/photos/12074236/pexels-photo-12074236.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";
const INSTA = [
  "https://images.unsplash.com/photo-1661529260837-f4bb7164fd39?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.unsplash.com/photo-1607703829739-c05b7beddf60?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.unsplash.com/photo-1779398970793-a795aa71eaf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.pexels.com/photos/10976654/pexels-photo-10976654.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=700",
  "https://images.unsplash.com/photo-1670177257750-9b47927f68eb?crop=entropy&cs=srgb&fm=jpg&q=85&w=700",
  "https://images.pexels.com/photos/34399114/pexels-photo-34399114.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=700",
];

const SectionHeading = ({ overline, title, cta, to }) => (
  <div className="flex items-end justify-between mb-10 gap-4">
    <div>
      {overline && <p className="overline text-gold mb-3">{overline}</p>}
      <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight">{title}</h2>
    </div>
    {cta && (
      <Link to={to} className="hidden sm:inline-flex items-center gap-2 text-sm link-underline whitespace-nowrap group">
        {cta} <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
      </Link>
    )}
  </div>
);

export default function Home() {
  const { t } = useLanguage();
  const [bestSellers, setBestSellers] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { best_seller: true, limit: 4 } }).then((r) => setBestSellers(r.data.items)).catch(() => {});
    api.get("/products", { params: { new_arrival: true, limit: 4 } }).then((r) => setNewArrivals(r.data.items)).catch(() => {});
    api.get("/reviews").then((r) => setReviews(r.data.reviews)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[600px] w-full overflow-hidden">
        <img src={HERO} alt="M&A Jewelry fine jewelry" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-black/40" />
        <div className="relative h-full mx-auto max-w-[1400px] px-5 lg:px-10 flex flex-col justify-end pb-24">
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[1.02] max-w-3xl"
          >
            {t("Brilliance, worn")} <span className="italic">{t("for a lifetime.")}</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.25 }}
            className="mt-6 text-white/80 max-w-lg text-base sm:text-lg"
          >
            {t("Hand-crafted fine jewelry, conceived in Chicago. Diamonds, gold and rare stones set to catch the light for generations.")}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.4 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link to="/shop">
              <Button className="rounded-none h-12 px-8 bg-white text-black hover:bg-gold hover:text-gold-foreground" data-testid="hero-shop-btn">
                {t("Shop the Collection")}
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 grid grid-cols-2 md:grid-cols-4 divide-x divide-border">
          {[
            { icon: Truck, label: "Complimentary Shipping", sub: "On every order, worldwide" },
            { icon: ShieldCheck, label: "Lifetime Warranty", sub: "Craftsmanship guaranteed" },
            { icon: Gem, label: "Ethically Sourced", sub: "Conflict-free diamonds" },
            { icon: Sparkles, label: "Free Engraving", sub: "Make it yours" },
          ].map((f, i) => (
            <div key={i} className="flex items-center gap-3 py-6 px-4 sm:px-6">
              <f.icon className="h-6 w-6 text-gold shrink-0" strokeWidth={1.2} />
              <div>
                <p className="text-sm font-medium">{t(f.label)}</p>
                <p className="text-xs text-muted-foreground">{t(f.sub)}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* BEST SELLERS */}
      <section className="bg-secondary/40 py-24">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <SectionHeading overline={t("Most Coveted")} title={t("Best Sellers")} cta={t("Shop best sellers")} to="/shop?best_seller=true" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 lg:gap-8">
            {bestSellers.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* EDITORIAL */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="relative">
            <img src={EDITORIAL} alt="The M&A Jewelry atelier" className="w-full aspect-[4/5] object-cover" />
          </div>
          <div className="lg:pl-8">
            <p className="overline text-gold mb-4">{t("The Maison")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">
              {t("A century of quiet brilliance")}
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed">
              {t("For over a century, M&A Jewelry has crafted jewelry that transcends trends. Every stone is hand-selected, every setting sculpted by master artisans. We believe true luxury is felt — in the weight of gold, the fire of a diamond, the story a piece carries.")}
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              {t("From the solitaire that marks a promise to the watch passed between generations, our pieces are made to be lived in and loved.")}
            </p>
            <Link to="/about">
              <Button variant="outline" className="rounded-none h-12 px-8 mt-8" data-testid="editorial-about-btn">
                {t("Discover Our Story")} <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* NEW ARRIVALS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-24">
        <SectionHeading overline={t("Just Landed")} title={t("New Arrivals")} cta={t("Shop new in")} to="/shop?new_arrival=true" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 lg:gap-8">
          {newArrivals.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* CLIENT VOICES + REVIEWS */}
      <section className="bg-primary text-primary-foreground py-24">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10 text-center">
          <p className="overline text-gold mb-3">{t("Client Voices")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light">{t("Share your experience")}</h2>
          <p className="text-primary-foreground/70 mt-5 leading-relaxed max-w-2xl mx-auto">
            {t("Purchased with us? We'd love to hear your story. Leave a review and help others discover M&A Jewelry.")}
          </p>

          {reviews.length > 0 && (
            <div className="grid md:grid-cols-3 gap-6 mt-14 text-left" data-testid="home-reviews">
              {reviews.slice(0, 3).map((r) => (
                <div key={r.id} className="border border-primary-foreground/15 p-8" data-testid={`home-review-${r.id}`}>
                  <StarRating rating={r.rating} />
                  {r.title && <h3 className="font-serif text-xl mt-4">{r.title}</h3>}
                  <p className="text-primary-foreground/70 text-sm mt-3 leading-relaxed">{r.text}</p>
                  <div className="mt-6 pt-4 border-t border-primary-foreground/10">
                    <p className="text-sm font-medium">{r.customer_name}</p>
                    {r.location && <p className="text-xs text-primary-foreground/50">{r.location}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          <Link to="/reviews">
            <Button className="rounded-none h-12 px-10 mt-12 bg-gold text-gold-foreground hover:bg-gold/90" data-testid="see-more-reviews-btn">
              {t("See more")}
            </Button>
          </Link>
        </div>
      </section>

      {/* INSTAGRAM */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24">
        <div className="text-center mb-12">
          <p className="overline text-gold mb-3">@majewelry</p>
          <h2 className="font-serif text-4xl sm:text-5xl font-light">{t("Follow the sparkle")}</h2>
        </div>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-2 lg:gap-3">
          {INSTA.map((src, i) => (
            <a key={i} href="#" className="group relative overflow-hidden aspect-square bg-secondary" data-testid={`insta-${i}`}>
              <img src={src} alt="Instagram" loading="lazy" className="hover-zoom-img h-full w-full object-cover" />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors" />
            </a>
          ))}
        </div>
      </section>
    </div>
  );
}
